// Attachment rejection notices (PLT-6061).
//
// A file the chat refuses has to say so somewhere the user is looking, and the
// place that discovers the refusal is not always the place that can render it:
// the DMA landing hero validates files on the dashboard, while a hand-off file
// that fails to read is discovered by `InitialPromptAutoSender` in
// `upright-chat.tsx` — a component with no UI of its own, mounted next to the
// composer rather than inside it. So the notices live in a module-level store
// that the surfaces subscribe to via `useSyncExternalStore`, the same shape as
// `subscribeSelectedChatProvider` in `upright-chat-shared.ts`.
//
// Deliberately dependency-free: the DMA landing entry renders on the dashboard
// and must not pull the chat runtime (assistant-ui, redux) into that page's
// module graph, so this module and `attachment-notice-row.tsx` are what the two
// surfaces share instead of one importing the other.
// ---------------------------------------------------------------------------

/**
 * Stable empty snapshot. `useSyncExternalStore` compares snapshots by identity
 * and re-renders forever if `getSnapshot` returns a fresh `[]` each call, so
 * the cleared state is always this exact array.
 */
const NO_NOTICES: readonly string[] = Object.freeze([]);

let _attachmentNotices: readonly string[] = NO_NOTICES;
const _attachmentNoticeListeners = new Set<() => void>();

function notify(): void {
  for (const l of _attachmentNoticeListeners) l();
}

/** Current rejection messages — one per refused file, in the order picked. */
export function getAttachmentNotices(): readonly string[] {
  return _attachmentNotices;
}

/**
 * Replace the visible notices. Called once per batch (a multi-file pick or
 * paste reports all of its rejections together) rather than per file, so the
 * user sees the whole outcome of one action instead of the row growing as the
 * loop runs.
 *
 * Passing an empty array is the same as clearing.
 */
export function setAttachmentNotices(messages: readonly string[]): void {
  if (messages.length === 0) {
    clearAttachmentNotices();
    return;
  }
  _attachmentNotices = messages;
  notify();
}

/**
 * Drop the notices. Called when the user's next action makes them stale: a new
 * pick or paste, sending the message, or leaving the landing hero for `/chat`
 * (the store is module-level, so a notice raised on the dashboard would
 * otherwise reappear under the `/chat` composer after the hand-off).
 */
export function clearAttachmentNotices(): void {
  if (_attachmentNotices === NO_NOTICES) return;
  _attachmentNotices = NO_NOTICES;
  notify();
}

export function subscribeAttachmentNotices(listener: () => void): () => void {
  _attachmentNoticeListeners.add(listener);
  return () => {
    _attachmentNoticeListeners.delete(listener);
  };
}
